import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Users, Radio, Crown, Flame, Filter, Plus, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { creatorAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AgeGate from '../components/shared/AgeGate';
import { toast } from 'sonner';

const CATEGORIES = ['general', 'education', 'fitness', 'music', 'cooking', 'tech', 'art', 'gaming', 'lifestyle'];

export default function CreatorDiscoveryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ageVerified, setAgeVerified] = useState(false);
  const [showAgeGate, setShowAgeGate] = useState(true);
  const [creators, setCreators] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [newRoom, setNewRoom] = useState({ name: '', description: '', category: 'general', price_per_month: 10, is_adult: false, is_broadcast: true, preview_text: '' });

  // Check user age
  const userAge = (() => {
    if (!user?.date_of_birth) return null;
    const dob = new Date(user.date_of_birth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
    return age;
  })();

  const isAdult = userAge !== null && userAge >= 18;

  useEffect(() => {
    // If user has verified age and is 18+, skip gate
    if (isAdult) {
      setAgeVerified(true);
      setShowAgeGate(false);
    }
  }, [isAdult]);

  useEffect(() => {
    if (!ageVerified) return;
    const fetchData = async () => {
      try {
        const params = {};
        if (category && category !== 'all') params.category = category;
        if (search) params.search = search;
        const [discoverRes, myRes] = await Promise.all([
          creatorAPI.discover(params),
          user ? creatorAPI.myRooms().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        setCreators(discoverRes.data);
        setMyRooms(myRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [ageVerified, category, search, user]);

  const handleCreateRoom = async () => {
    if (!newRoom.name.trim()) { toast.error('Room name required'); return; }
    try {
      const res = await creatorAPI.createRoom(newRoom);
      toast.success('Room created!');
      setMyRooms([res.data, ...myRooms]);
      setNewRoom({ name: '', description: '', category: 'general', price_per_month: 10, is_adult: false, is_broadcast: true, preview_text: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create room'); }
  };

  if (showAgeGate && !ageVerified) {
    return (
      <AgeGate
        user={user}
        onVerified={() => { setAgeVerified(true); setShowAgeGate(false); }}
        onDenied={() => navigate('/')}
      />
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-950 pb-8" data-testid="creator-discovery-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-rose-950/40 to-zinc-950 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-bold text-3xl text-white" data-testid="creator-discovery-title">Creator Hub</h1>
              <p className="text-zinc-400 text-sm mt-1">Discover exclusive content from top creators</p>
            </div>
            <div className="flex items-center gap-3">
              {user && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-rose-500 text-white font-semibold text-sm" data-testid="create-room-btn">
                      <Plus size={16} className="mr-1" /> Create Room
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md" aria-describedby="create-room-desc">
                    <DialogHeader><DialogTitle className="text-white">Create Creator Room</DialogTitle></DialogHeader>
                    <p id="create-room-desc" className="sr-only">Create a new creator room with subscription pricing</p>
                    <div className="space-y-3 pt-2">
                      <Input placeholder="Room name" value={newRoom.name} onChange={e => setNewRoom({ ...newRoom, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="room-name-input" />
                      <Textarea placeholder="Description" value={newRoom.description} onChange={e => setNewRoom({ ...newRoom, description: e.target.value })} rows={2} className="bg-zinc-800 border-zinc-700 text-white" />
                      <Input placeholder="Free preview text" value={newRoom.preview_text} onChange={e => setNewRoom({ ...newRoom, preview_text: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                      <div className="grid grid-cols-2 gap-3">
                        <Select value={newRoom.category} onValueChange={v => setNewRoom({ ...newRoom, category: v })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700 text-white">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" min={10} value={newRoom.price_per_month} onChange={e => setNewRoom({ ...newRoom, price_per_month: parseFloat(e.target.value) || 10 })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="$/month" data-testid="room-price-input" />
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                          <input type="checkbox" checked={newRoom.is_broadcast} onChange={e => setNewRoom({ ...newRoom, is_broadcast: e.target.checked })} className="rounded" />
                          <Radio size={14} /> Broadcast
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                          <input type="checkbox" checked={newRoom.is_adult} onChange={e => setNewRoom({ ...newRoom, is_adult: e.target.checked })} className="rounded" />
                          <Lock size={14} /> 18+
                        </label>
                      </div>
                      <p className="text-xs text-zinc-500">Revenue split: You keep 80%. Tips: 90%. PPV: 85%.</p>
                      <Button onClick={handleCreateRoom} className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold" data-testid="submit-create-room-btn">Create Room</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Search + Filters */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                placeholder="Search creators..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                data-testid="creator-search-input"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-white">
                <Filter size={14} className="mr-2" /><SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                <SelectItem value="all">All</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* My Rooms (if creator) */}
      {myRooms.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-6">
          <h2 className="font-bold text-lg text-white mb-3 flex items-center gap-2"><Crown size={18} className="text-amber-400" /> My Rooms</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {myRooms.map(room => (
              <Link key={room.id} to={`/creator/${room.id}`}>
                <div className="bg-zinc-900 border border-zinc-800 p-4 hover:border-zinc-600 transition-colors" data-testid={`my-room-${room.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white truncate">{room.name}</h3>
                    {room.is_live && <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-400">
                    <span>{room.subscriber_count} subs</span>
                    <span>{formatCurrency(room.price_per_month)}/mo</span>
                    <span className="text-green-400 font-mono">{formatCurrency(room.total_tips || 0)} tips</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Creator Grid */}
      <section className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-white flex items-center gap-2"><Flame size={18} className="text-rose-400" /> Discover Creators</h2>
          <span className="text-xs text-zinc-500">{creators.length} creators found</span>
        </div>

        {creators.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900 border border-zinc-800">
            <Radio className="mx-auto mb-4 text-zinc-600" size={48} />
            <h3 className="font-bold text-xl text-white mb-2">No Creators Yet</h3>
            <p className="text-zinc-500 text-sm">Be the first to create a room and start earning!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {creators.map((room, idx) => (
              <motion.div key={room.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
                <Link to={`/creator/${room.id}`}>
                  <div className="bg-zinc-900 border border-zinc-800 overflow-hidden hover:border-zinc-600 transition-all group" data-testid={`creator-card-${room.id}`}>
                    <div className={`h-32 relative ${room.is_adult ? 'bg-gradient-to-br from-rose-900 to-pink-900' : 'bg-gradient-to-br from-violet-800 to-indigo-900'}`}>
                      {room.is_live && (
                        <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-red-500">
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          <span className="text-xs text-white font-bold">LIVE</span>
                        </div>
                      )}
                      {room.is_adult && <Lock className="absolute top-3 right-3 text-white/40" size={18} />}
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                        <div className="flex items-center gap-1 text-white/80 text-xs">
                          <Users size={12} /><span>{room.subscriber_count} subs</span>
                        </div>
                        <span className="text-xs bg-white/20 backdrop-blur-sm text-white px-2 py-0.5">{room.category}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-white truncate">{room.name}</h3>
                      <p className="text-xs text-zinc-500 mt-0.5">by {room.creator_name}</p>
                      {room.creator_bio && <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{room.creator_bio}</p>}
                      {room.preview_text && !room.creator_bio && <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{room.preview_text}</p>}
                      {room.creator_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {room.creator_tags.slice(0, 3).map(tag => (
                            <span key={tag} className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-[10px]">{tag}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
                        <span className="font-bold text-lg text-white">{formatCurrency(room.price_per_month)}<span className="text-xs text-zinc-500 font-normal">/mo</span></span>
                        <Button size="sm" className="bg-rose-500 text-white text-xs font-bold group-hover:bg-rose-400 transition-colors" data-testid={`subscribe-btn-${room.id}`}>Subscribe</Button>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
